import { useEffect, useState } from "react";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";

export function BaselineEditorButton({ onSaved }: { onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rowId, setRowId] = useState<string | null>(null);
  const [db, setDb] = useState("0");
  const [egress, setEgress] = useState("0");
  const [invocations, setInvocations] = useState("0");
  const [storage, setStorage] = useState("0");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("platform_cost_baseline")
        .select("id, db_monthly_usd, egress_monthly_usd, invocations_monthly_usd, storage_per_gb_month_usd")
        .order("effective_from", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        toast.error(error.message);
      } else if (data) {
        setRowId(data.id);
        setDb(String(data.db_monthly_usd ?? 0));
        setEgress(String(data.egress_monthly_usd ?? 0));
        setInvocations(String(data.invocations_monthly_usd ?? 0));
        setStorage(String(data.storage_per_gb_month_usd ?? 0));
      }
      setLoading(false);
    })();
  }, [open]);

  const save = async () => {
    const payload = {
      db_monthly_usd: Number(db) || 0,
      egress_monthly_usd: Number(egress) || 0,
      invocations_monthly_usd: Number(invocations) || 0,
      storage_per_gb_month_usd: Number(storage) || 0,
    };
    setSaving(true);
    try {
      if (rowId) {
        const { error } = await supabase
          .from("platform_cost_baseline")
          .update(payload)
          .eq("id", rowId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("platform_cost_baseline")
          .insert({ ...payload, note: "Created from Workspace Costs editor" });
        if (error) throw error;
      }
      toast.success("Baseline updated");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Settings2 className="h-4 w-4" /> Edit baseline
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit cost baseline</DialogTitle>
            <DialogDescription>
              Enter the monthly Lovable Cloud bill numbers. Used to prorate storage, database, egress, and edge
              invocations against each workspace's row share.
            </DialogDescription>
          </DialogHeader>
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <div className="grid gap-3">
              <div>
                <Label>Database $ / month</Label>
                <Input type="number" step="0.01" value={db} onChange={(e) => setDb(e.target.value)} />
              </div>
              <div>
                <Label>Egress $ / month</Label>
                <Input type="number" step="0.01" value={egress} onChange={(e) => setEgress(e.target.value)} />
              </div>
              <div>
                <Label>Edge invocations $ / month</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invocations}
                  onChange={(e) => setInvocations(e.target.value)}
                />
              </div>
              <div>
                <Label>Storage $ / GB-month</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={storage}
                  onChange={(e) => setStorage(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || loading}>
              {saving ? "Saving…" : "Save baseline"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
